import { z } from "zod";

import type { DosageType } from "@/api/products";
import { CareplanPendingAction } from "@/constants/careplan";
import {
  DOSAGE,
  PRODUCT_ROLE,
  PRODUCT_TYPE,
  TIMES_OF_USE,
  TIMES_OF_USE_BY_ROLE,
  TIMES_OF_USE_LABELS,
} from "@/utils/consts";

const ALL_DOSAGES = Object.values(DOSAGE);

// 0mg must always be selectable regardless of the product's configured doses.
const allowedDosesFor = (
  validDosesByProduct: Map<number, DosageType[]>,
  productId: number,
): DosageType[] => {
  const validDoses = validDosesByProduct.get(productId);

  if (!validDoses || validDoses.length === 0) {
    return ALL_DOSAGES;
  }

  return validDoses.includes(DOSAGE.Zero) ? validDoses : [DOSAGE.Zero, ...validDoses];
};

export const createEditCareplanSchemas = (
  validDosesByProduct: Map<number, DosageType[]> = new Map(),
) => {
  return z
    .object({
      updateReason: z.string().min(5, "Should contain at least 5 characters."),
      products: z.array(
        z.object({
          productId: z.number(),
          dose: z.enum(DOSAGE, {
            message: "The field is required.",
          }),
          increaseDose: z.union([z.enum(DOSAGE), z.literal("")]).refine(
            (value) => {
              return value !== "";
            },
            { message: "The field is required." },
          ),
          usageInstructions: z.string().optional(),
          howItWorks: z.string().optional(),
          role: z.enum(PRODUCT_ROLE),
          whyRecommended: z.string(),
          whyRecommendedTitle: z.string().max(255, {
            error: "The title should not exceed 255 characters.",
          }),
          timesOfUse: z.enum(TIMES_OF_USE),
          form: z
            .union([z.enum(PRODUCT_TYPE, { error: "Select a valid form." }), z.literal("")])
            .refine(
              (value) => {
                return value !== "";
              },
              { message: "The field is required." },
            ),
          symptoms: z.string().optional(),
          cbdThcRatio: z.string().optional(),
        }),
      ),
    })
    .superRefine((data, ctx) => {
      data.products.forEach((product, index) => {
        const allowedDoses = allowedDosesFor(validDosesByProduct, product.productId);
        const message = `Must be one of: ${allowedDoses.join(", ")}.`;

        if (product.dose && !allowedDoses.includes(product.dose)) {
          ctx.addIssue({
            code: "custom",
            path: ["products", index, "dose"],
            message,
          });
        }

        if (product.increaseDose && !allowedDoses.includes(product.increaseDose)) {
          ctx.addIssue({
            code: "custom",
            path: ["products", index, "increaseDose"],
            message,
          });
        }

        const allowedTimesOfUse = TIMES_OF_USE_BY_ROLE[product.role];

        if (product.timesOfUse && !allowedTimesOfUse.includes(product.timesOfUse)) {
          const allowedLabels = allowedTimesOfUse.map((timesOfUse) => {
            return TIMES_OF_USE_LABELS[timesOfUse];
          });

          ctx.addIssue({
            code: "custom",
            path: ["products", index, "timesOfUse"],
            message: `Must be one of: ${allowedLabels.join(", ")}.`,
          });
        }
      });
    });
};

export const EditCareplanSchemas = createEditCareplanSchemas();

export type EditCareplanSchemaType = z.infer<typeof EditCareplanSchemas>;

// `increaseDose` and `form` accept "" while the user has not picked a value yet, and the
// refinements above reject it on submit. That makes the form's own shape the input type,
// not the parsed output — see the three generics on useForm in edit-careplan-form.tsx.
export type EditCareplanSchemaInput = z.input<typeof EditCareplanSchemas>;

export const careplanPendingActionSchema = z.nativeEnum(CareplanPendingAction);
